/* eslint-disable no-param-reassign -- Ref .current assignments are standard React patterns */
import { useEffect, useMemo, useRef, useState } from 'react';
import { metaData, Enums, Types, cache } from '@cornerstonejs/core';
import type { Types as CornerstoneTypes } from '@cornerstonejs/core';
import { Dicom } from '@/models/dicom';
import type { ViewportInitialCamera } from '../../ViewerContext';
import canvasStyles from '../../ViewerCanvas/ViewerCanvas.module.css';

interface UseViewportSetupProps {
  dicom: Dicom | null;
  viewportId: string;
  renderingEngine: CornerstoneTypes.IRenderingEngine | null;
  cornerstoneRef: React.RefObject<HTMLDivElement | null>;
  videoUrl: string | null;
  imageUrl: string | null;
  isVideo: boolean;
  frameCount: number;
  autoplay: boolean;
  /** If set, start the video on this frame (1-based) instead of frame 1 */
  startFrame?: number;
  /** Ref tracking whether a viewport has been created for the current DICOM */
  viewportCreatedRef: React.MutableRefObject<boolean>;
  /** Ref tracking the currently loaded DICOM UUID */
  loadedDicomUuidRef: React.MutableRefObject<string | undefined>;
  /** Ref storing the video metadata provider for cleanup */
  videoMetadataProviderRef: React.MutableRefObject<
    ((type: string, imageId: unknown) => unknown) | null
  >;
  setIsDicomTransitioning: (v: boolean) => void;
  /** Per-DICOM VOI persistence map shared across all viewports */
  voiMapRef: React.RefObject<Map<string, { lower: number; upper: number }>>;
  /** Per-viewport initial camera snapshot for deterministic reset */
  viewportInitialCameraRef: React.RefObject<Map<string, ViewportInitialCamera>>;
}

interface UseViewportSetupReturn {
  viewport: CornerstoneTypes.IStackViewport | Types.IVideoViewport | null;
  isLoadingViewport: boolean;
  viewportError: Error | null;
  /** DICOM whose setup produced `viewportError` (the error lags a DICOM switch). */
  failedDicomUuid: string | undefined;
  isVideoViewport: boolean;
  frameImageIds: string[];
  memoizedImageIds: string[] | null;
}

/**
 * True when the canvas buffer no longer matches its displayed box: the pane
 * resized and the engine hasn't refit yet, so the browser is stretching the
 * last frame. Video canvases are sized in CSS pixels, stack canvases in device
 * pixels; 1px of slack absorbs the engine's rounding.
 */
function isCanvasStretched(canvas: HTMLCanvasElement, isVideoCanvas: boolean) {
  const scale = isVideoCanvas ? 1 : window.devicePixelRatio || 1;
  return (
    Math.abs(canvas.width - canvas.clientWidth * scale) > 1 ||
    Math.abs(canvas.height - canvas.clientHeight * scale) > 1
  );
}

export function useViewportSetup({
  dicom,
  viewportId,
  renderingEngine,
  cornerstoneRef,
  videoUrl,
  imageUrl,
  isVideo,
  frameCount,
  autoplay,
  startFrame,
  viewportCreatedRef,
  loadedDicomUuidRef,
  videoMetadataProviderRef,
  setIsDicomTransitioning,
  voiMapRef,
  viewportInitialCameraRef,
}: UseViewportSetupProps): UseViewportSetupReturn {
  const [viewport, setViewport] = useState<
    CornerstoneTypes.IStackViewport | Types.IVideoViewport | null
  >(null);
  const [isLoadingViewport, setIsLoadingViewport] = useState(false);
  const [viewportError, setViewportError] = useState<Error | null>(null);
  const [failedDicomUuid, setFailedDicomUuid] = useState<string | undefined>();

  // Track the current viewport type so we can decide whether to reuse or recreate.
  // Updated synchronously during render so the cleanup closure reads the *incoming* type.
  const currentViewportTypeRef = useRef<'VIDEO' | 'STACK' | null>(null);
  const nextIsVideoRef = useRef(isVideo);
  nextIsVideoRef.current = isVideo;

  // Monotonic counter to detect stale async setupViewport() calls.
  // Incremented at the start of each setup effect; stale setups bail out
  // at every await checkpoint instead of waiting for React's cleanup.
  const setupIdRef = useRef(0);

  // Set when the outgoing clip's frame is stretched (pane resized in the same
  // commit as the clip change); suppresses the old-frame snapshots until the
  // new clip paints.
  const hideOldFrameRef = useRef(false);

  // Cleanup viewport when DICOM changes.
  // If the viewport type stays the same (VIDEO→VIDEO or STACK→STACK), we REUSE
  // the viewport — only cleaning metadata and pausing video — to skip the expensive
  // destroy/recreate cycle (disableElement → rAF → enableElement).
  useEffect(() => {
    if (!dicom?.uuid) return () => {};

    // Capture ref values in the effect body so the cleanup closure
    // uses snapshots, not live refs (react-hooks/exhaustive-deps).
    const voiMap = voiMapRef.current;

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const element = cornerstoneRef.current;
      const currentType = currentViewportTypeRef.current;
      const nextType = nextIsVideoRef.current ? 'VIDEO' : 'STACK';
      const canReuse = currentType !== null && currentType === nextType;

      // Always clean video metadata provider
      if (videoMetadataProviderRef.current) {
        metaData.removeProvider(videoMetadataProviderRef.current);
        videoMetadataProviderRef.current = null;
      }

      // Pause any active video playback
      if (element && renderingEngine) {
        try {
          const existingViewport = renderingEngine.getViewport(viewportId);
          if (
            existingViewport &&
            typeof (existingViewport as Types.IVideoViewport).pause ===
              'function'
          ) {
            (existingViewport as Types.IVideoViewport).pause();
          }
        } catch {
          // Ignore cleanup errors
        }
      }

      // A layout switch resizes the pane in the same commit that changes its
      // clip (AP-875). Until the engine refits, the canvas shows the old clip
      // stretched to the new box — hide the pane until the new clip paints
      // rather than show (or snapshot) that distorted frame.
      const canvas = element?.querySelector('canvas');
      if (
        element &&
        canvas &&
        currentType !== null &&
        isCanvasStretched(canvas, currentType === 'VIDEO')
      ) {
        element.dataset.rendered = 'false';
        hideOldFrameRef.current = true;
      }

      // Save current VOI for per-DICOM persistence before switching.
      // The cleanup closure captures the outgoing dicom UUID.
      if (element && renderingEngine && dicom?.uuid) {
        try {
          const existingVp = renderingEngine.getViewport(viewportId);
          if (existingVp) {
            const { voiRange } = existingVp.getProperties();
            if (voiRange) {
              voiMap.set(dicom.uuid, {
                lower: voiRange.lower,
                upper: voiRange.upper,
              });
            }
          }
        } catch {
          // Viewport may already be disposed during unmount
        }
      }

      if (canReuse) {
        // REUSE PATH: Keep viewport alive for same-type switch.
        // Do NOT reset viewportCreatedRef — viewport stays alive for reuse.
      } else {
        // TYPE CHANGE PATH: Mark viewport for recreation.
        // Do NOT call disableElement() here — enableElement() in the setup
        // effect handles the disable+enable atomically in the same call stack,
        // keeping the WebGL context pool state consistent. Splitting them
        // across effects causes "contextData is null" crashes.
        viewportCreatedRef.current = false;
        setViewport(null);
        currentViewportTypeRef.current = null;
      }
    };
  }, [
    dicom?.uuid,
    renderingEngine,
    viewportId,
    cornerstoneRef,
    viewportCreatedRef,
    videoMetadataProviderRef,
    voiMapRef,
  ]);

  // For single-frame DICOMs, create imageId from media API's imageUrl
  const frameImageIds = useMemo(() => {
    if (imageUrl) {
      return [`web:${imageUrl}`];
    }
    return [];
  }, [imageUrl]);

  const memoizedImageIds = useMemo(
    () => (frameImageIds.length > 0 ? frameImageIds : null),
    [frameImageIds]
  );

  // Metadata provider for this viewport's DICOM (stack images)
  const metadataProviderRef = useRef<
    ((type: string, imageId: unknown) => unknown) | null
  >(null);

  useEffect(() => {
    if (!dicom || !memoizedImageIds || memoizedImageIds.length === 0) {
      return () => {};
    }

    const imageIdSet = new Set(memoizedImageIds);
    const rows = dicom.pixelHeight;
    const columns = dicom.pixelWidth;
    // Render-ready spacing is derived at ingest — see Dicom.rowPixelSpacingMm.
    const rowPixelSpacing = dicom.rowPixelSpacingMm;
    const columnPixelSpacing = dicom.columnPixelSpacingMm;

    // Dimension validation: check if loaded image dimensions match DICOM metadata.
    // If the backend serves resized images, pixel spacing needs proportional adjustment.
    memoizedImageIds.forEach((imageId) => {
      const cachedImage = cache.getImageLoadObject(imageId);
      if (cachedImage?.promise) {
        cachedImage.promise
          .then((img) => {
            if (img && (img.width !== columns || img.height !== rows)) {
              // eslint-disable-next-line no-console
              console.warn(
                `[ViewerPane] Image dimension mismatch for ${imageId}: ` +
                  `loaded=${img.width}x${img.height}, DICOM metadata=${columns}x${rows}. ` +
                  `Measurements may be inaccurate if backend serves resized images.`
              );
            }
          })
          .catch(() => {
            // Image failed to load — skip dimension validation.
            // The main rendering pipeline handles load errors separately.
          });
      }
    });

    const provider = (type: string, imageId: unknown) => {
      if (typeof imageId !== 'string' || !imageIdSet.has(imageId))
        return undefined;

      switch (type) {
        case 'imagePixelModule':
          return {
            pixelRepresentation: 0,
            bitsAllocated: 8,
            bitsStored: 8,
            highBit: 7,
            samplesPerPixel: 3,
            photometricInterpretation: 'RGB',
            rows,
            columns,
          };
        case 'imagePlaneModule':
          return {
            rowPixelSpacing,
            columnPixelSpacing,
            rows,
            columns,
            rowCosines: [1, 0, 0],
            columnCosines: [0, 1, 0],
            imagePositionPatient: [0, 0, 0],
            imageOrientationPatient: [1, 0, 0, 0, 1, 0],
            frameOfReferenceUID: imageId,
          };
        case 'generalSeriesModule':
          return { modality: 'US' };
        case 'sopCommonModule':
          return {
            sopInstanceUID: imageId,
            sopClassUID: '1.2.840.10008.5.1.4.1.1.3.1',
          };
        case 'pixelSpacing':
          return [rowPixelSpacing, columnPixelSpacing];
        case 'spacing':
          return [columnPixelSpacing, rowPixelSpacing, 1];
        case 'voiLutModule':
          return { windowWidth: 256, windowCenter: 128 };
        case 'modalityLutModule':
          return { rescaleIntercept: 0, rescaleSlope: 1, rescaleType: 'US' };
        case 'calibration':
          return {
            rowPixelSpacing,
            columnPixelSpacing,
            scale: 1,
            type: 'DICOM',
          };
        case 'calibratedPixelSpacing':
          return { rowPixelSpacing, columnPixelSpacing };
        case 'ultrasoundRegions':
          return {
            regions: dicom.ultrasoundRegions ?? [],
          };
        default:
          return undefined;
      }
    };

    metaData.addProvider(provider, 10000);
    metadataProviderRef.current = provider;

    return () => {
      if (metadataProviderRef.current) {
        metaData.removeProvider(metadataProviderRef.current);
        metadataProviderRef.current = null;
      }
    };
  }, [dicom, memoizedImageIds]);

  // Register viewport with shared rendering engine and load content (stack or video)
  useEffect(() => {
    // For video: need videoUrl; for stack: need memoizedImageIds
    const hasVideoContent = isVideo && videoUrl;
    const hasStackContent = !isVideo && memoizedImageIds;

    if (
      !renderingEngine ||
      !cornerstoneRef.current ||
      (!hasVideoContent && !hasStackContent)
    ) {
      return () => {};
    }

    const element = cornerstoneRef.current;
    let isMounted = true;

    // Increment setup counter — any prior async setupViewport() will
    // detect staleness at its next await checkpoint and bail out.
    setupIdRef.current += 1;
    const currentSetupId = setupIdRef.current;
    const isStale = () => setupIdRef.current !== currentSetupId;

    async function setupViewport() {
      if (!renderingEngine || !element) return;

      // Capture whether this is a reuse (existing viewport of same type)
      // BEFORE the creation block potentially sets viewportCreatedRef to true.
      const isReuse = viewportCreatedRef.current;

      // Remove any orphaned snapshot canvases from prior superseded setups.
      // During rapid navigation, multiple async setupViewport() calls overlap
      // and each may have created snapshots before being superseded.
      const wrapper = element.parentElement ?? element;
      wrapper
        .querySelectorAll<HTMLCanvasElement>('[data-viewport-snapshot]')
        .forEach((s) => s.remove());

      // Outer-scoped so the catch below can remove them if a later step throws.
      let transitionSnapshot: HTMLCanvasElement | null = null;
      let snapshotCanvas: HTMLCanvasElement | null = null;

      // Signal loading so the blue top bar shows during all navigations.
      // The per-pane skeleton is gated by hasEverRenderedRef in ViewerPane,
      // so this won't re-trigger the skeleton on subsequent loads.
      setIsLoadingViewport(true);
      setViewportError(null);
      setFailedDicomUuid(undefined);

      try {
        // Create viewport if it doesn't exist yet.
        // When the cleanup effect preserved the viewport (same-type reuse),
        // viewportCreatedRef is still true — we skip the expensive destroy/create cycle.
        if (!viewportCreatedRef.current) {
          // TYPE CHANGE / FIRST MOUNT: Capture old frame as snapshot before
          // destroying the viewport, so the user sees the old content until
          // the new viewport renders its first frame.
          const sourceCanvas = element.querySelector('canvas');
          if (
            !hideOldFrameRef.current &&
            sourceCanvas &&
            sourceCanvas.width > 0 &&
            sourceCanvas.height > 0
          ) {
            transitionSnapshot = document.createElement('canvas');
            transitionSnapshot.width = sourceCanvas.width;
            transitionSnapshot.height = sourceCanvas.height;
            transitionSnapshot.getContext('2d')?.drawImage(sourceCanvas, 0, 0);
            transitionSnapshot.className = canvasStyles.viewportSnapshot;
            transitionSnapshot.dataset.viewportSnapshot = 'true';
            element.appendChild(transitionSnapshot);
          }

          // Minimal delay to allow React DOM updates to settle.
          await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
          });

          if (!isMounted || isStale()) {
            if (transitionSnapshot) transitionSnapshot.remove();
            return;
          }

          // Clear any stale canvas and video elements before creating new viewport.
          // This is needed when switching between VIDEO and STACK types —
          // Cornerstone's _resetViewport only clears the canvas, not <video> elements.
          const existingCanvases = element.querySelectorAll('canvas');
          existingCanvases.forEach((canvas) => {
            // Don't remove our snapshot!
            if (canvas !== transitionSnapshot) canvas.remove();
          });
          const existingVideos = element.querySelectorAll('video');
          existingVideos.forEach((videoEl) => {
            videoEl.pause();
            videoEl.removeAttribute('src');
            videoEl.load();
            videoEl.remove();
          });

          // Enable element with appropriate viewport type.
          // enableElement() internally calls disableElement() first if a
          // viewport with this ID already exists — this atomic disable+enable
          // keeps the WebGL context pool consistent (no timing gap).
          const viewportType = isVideo
            ? Enums.ViewportType.VIDEO
            : Enums.ViewportType.STACK;

          renderingEngine.enableElement({
            viewportId,
            type: viewportType,
            element,
          });

          // Guard before mutating shared refs — a newer setup may have
          // started while we awaited the rAF above.
          if (isStale()) {
            if (transitionSnapshot) transitionSnapshot.remove();
            return;
          }

          viewportCreatedRef.current = true;
        }
        // else: viewport already exists with matching type — reuse it (skip rAF + enableElement)

        if (isStale()) {
          if (transitionSnapshot) transitionSnapshot.remove();
          return;
        }

        // Track the current viewport type for the next cleanup decision
        currentViewportTypeRef.current = isVideo ? 'VIDEO' : 'STACK';

        if (isVideo && videoUrl && dicom) {
          // VIDEO viewport path using setVideo() with metadata provider
          const videoViewport = renderingEngine.getViewport(
            viewportId
          ) as Types.IVideoViewport;

          if (!videoViewport) {
            throw new Error('Failed to get video viewport');
          }

          // Create a video imageId for this DICOM
          // The /frames/1 suffix is required so Cornerstone's getCurrentImageId()
          // can replace it with /frames/N for frame-specific annotation anchoring.
          const videoImageId = `video:${dicom.uuid}/frames/1`;

          // Remove any previous video metadata provider
          if (videoMetadataProviderRef.current) {
            metaData.removeProvider(videoMetadataProviderRef.current);
            videoMetadataProviderRef.current = null;
          }

          // Render-ready spacing is derived at ingest (BE) — already abs()'d,
          // cm→mm converted, and isotropic-collapsed for mixed-unit ultrasound
          // (M-mode, spectral Doppler). Aspect ratio for the video-canvas
          // resize below is just columnSpacing/rowSpacing, which is 1 when
          // ingest fell back to isotropic. Strict > 0 guard prevents a stale
          // signed row in the DB from producing a negative ratio (silent mirror).
          const rowPixelSpacing = dicom.rowPixelSpacingMm;
          const columnPixelSpacing = dicom.columnPixelSpacingMm;
          const pixelAspectRatio =
            rowPixelSpacing &&
            columnPixelSpacing &&
            rowPixelSpacing > 0 &&
            columnPixelSpacing > 0
              ? columnPixelSpacing / rowPixelSpacing
              : 1;

          // Match any frame-suffixed imageId for this DICOM
          const videoImageIdPrefix = `video:${dicom.uuid}/frames/`;
          const videoMetadataProvider = (type: string, imageId: unknown) => {
            if (
              typeof imageId !== 'string' ||
              !imageId.startsWith(videoImageIdPrefix)
            ) {
              return undefined;
            }

            switch (type) {
              case 'imageUrlModule':
                return { rendered: videoUrl };
              case 'generalSeriesModule':
                return { Modality: 'US' };
              case 'cineModule':
                return {
                  cineRate: dicom.framesPerSecond,
                  numberOfFrames: frameCount,
                };
              case 'imagePlaneModule':
                return {
                  rowPixelSpacing,
                  columnPixelSpacing,
                  rows: dicom.pixelHeight,
                  columns: dicom.pixelWidth,
                };
              case 'calibration':
                return {
                  rowPixelSpacing,
                  columnPixelSpacing,
                  scale: 1,
                  type: 'DICOM',
                };
              case 'calibratedPixelSpacing':
                return { rowPixelSpacing, columnPixelSpacing };
              case 'pixelSpacing':
                return [rowPixelSpacing, columnPixelSpacing];
              case 'spacing':
                return [columnPixelSpacing, rowPixelSpacing, 1];
              case 'ultrasoundRegions':
                return {
                  regions: dicom.ultrasoundRegions ?? [],
                };
              default:
                return undefined;
            }
          };

          videoMetadataProviderRef.current = videoMetadataProvider;
          metaData.addProvider(videoMetadataProvider, 10001);

          // On viewport REUSE, freeze the old frame as a snapshot to prevent
          // blink during video swap. Cornerstone's setVideo() internally triggers
          // renderFrame() which does clearRect + drawImage — if the new video
          // hasn't decoded frames yet, this shows a black frame.
          if (isReuse && !hideOldFrameRef.current) {
            const sourceCanvas = element.querySelector('canvas');
            if (
              sourceCanvas &&
              sourceCanvas.width > 0 &&
              sourceCanvas.height > 0
            ) {
              snapshotCanvas = document.createElement('canvas');
              snapshotCanvas.width = sourceCanvas.width;
              snapshotCanvas.height = sourceCanvas.height;
              snapshotCanvas.getContext('2d')?.drawImage(sourceCanvas, 0, 0);
              snapshotCanvas.className = canvasStyles.viewportSnapshot;
              snapshotCanvas.dataset.viewportSnapshot = 'true';
              element.appendChild(snapshotCanvas);
            }
          }

          try {
            await videoViewport.setVideo(videoImageId, 1);
          } catch (setVideoErr) {
            if (snapshotCanvas) snapshotCanvas.remove();
            metaData.removeProvider(videoMetadataProvider);
            throw setVideoErr;
          }

          // Bail if a newer DICOM switch superseded this setup
          if (!isMounted || isStale()) {
            if (snapshotCanvas) snapshotCanvas.remove();
            if (transitionSnapshot) transitionSnapshot.remove();
            return;
          }

          // Restore per-DICOM VOI, or reset to defaults on reuse path
          // so the previous DICOM's W/L doesn't leak.
          if (dicom?.uuid) {
            const savedVoi = voiMapRef.current?.get(dicom.uuid);
            if (savedVoi) {
              videoViewport.setProperties({ voiRange: savedVoi });
            } else if (isReuse) {
              videoViewport.setProperties({
                voiRange: { lower: 0, upper: 255 },
              });
            }
          }

          // Override VideoViewport dimensions to match DICOM physical aspect ratio.
          // Cornerstone's VideoViewport uses videoElement.videoWidth/videoHeight for all
          // rendering calculations (refreshRenderValues, drawImage), ignoring DICOM metadata.
          {
            const vp = videoViewport as unknown as {
              videoWidth: number;
              videoHeight: number;
              refreshRenderValues: () => void;
            };
            const correctedWidth = Math.round(
              dicom.pixelWidth * pixelAspectRatio
            );
            const correctedHeight = dicom.pixelHeight;

            if (
              vp.videoWidth !== correctedWidth ||
              vp.videoHeight !== correctedHeight
            ) {
              vp.videoWidth = correctedWidth;
              vp.videoHeight = correctedHeight;
              vp.refreshRenderValues();
            }
          }

          // Access video element for configuration
          const { videoElement } = videoViewport as unknown as {
            videoElement?: HTMLVideoElement;
          };

          if (videoElement) {
            videoElement.muted = true;
            videoElement.playsInline = true;

            if (!videoElement.isConnected) {
              videoElement.style.cssText =
                'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1';
              element.appendChild(videoElement);
            }

            // Wait for video to be ready
            if (videoElement.readyState < 3) {
              await new Promise<void>((resolve) => {
                const timeout = setTimeout(() => resolve(), 5000);
                const onReady = () => {
                  clearTimeout(timeout);
                  videoElement.removeEventListener('canplay', onReady);
                  resolve();
                };
                videoElement.addEventListener('canplay', onReady);
              });
            }
          }

          // Bail if a newer DICOM switch superseded this setup
          if (!isMounted || isStale()) {
            if (snapshotCanvas) snapshotCanvas.remove();
            if (transitionSnapshot) transitionSnapshot.remove();
            return;
          }

          videoViewport.render();
          videoViewport.resetCamera();

          // Save initial camera state for deterministic reset.
          // Must happen right after resetCamera() so it captures the exact
          // fit-to-window positioning including aspect ratio corrections.
          {
            const vpInternal = videoViewport as unknown as {
              videoCamera: { panWorld: number[]; parallelScale: number };
              videoWidth: number;
              videoHeight: number;
            };
            viewportInitialCameraRef.current.set(viewportId, {
              panWorld: [
                vpInternal.videoCamera.panWorld[0],
                vpInternal.videoCamera.panWorld[1],
              ],
              parallelScale: vpInternal.videoCamera.parallelScale,
              videoWidth: vpInternal.videoWidth,
              videoHeight: vpInternal.videoHeight,
            });
          }

          // Make container visible before removing snapshot to prevent blink
          // during type-change transitions. setViewport(null) in cleanup set
          // data-rendered="false"; the subsequent setViewport() confirms the value.
          element.dataset.rendered = 'true';
          hideOldFrameRef.current = false;

          // Remove all snapshots — new frame is now painted on the canvas
          if (snapshotCanvas) {
            snapshotCanvas.remove();
            snapshotCanvas = null;
          }
          if (transitionSnapshot) {
            transitionSnapshot.remove();
            transitionSnapshot = null;
          }

          // Seek to a specific frame if requested (e.g., from goToAnnotation)
          if (startFrame && isMounted && !isStale()) {
            try {
              await videoViewport.setFrameNumber(startFrame);
            } catch {
              // Seek may fail
            }
          }

          if (autoplay && !startFrame && isMounted && !isStale()) {
            try {
              await videoViewport.play();
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[ViewerPane] Autoplay failed:', err);
            }
          }

          if (isMounted && !isStale()) {
            setViewport(videoViewport);
            setIsLoadingViewport(false);
            loadedDicomUuidRef.current = dicom?.uuid;
            setIsDicomTransitioning(false);
          }
        } else if (memoizedImageIds) {
          // STACK viewport path for single-frame images
          const stackViewport = renderingEngine.getViewport(
            viewportId
          ) as CornerstoneTypes.IStackViewport;

          if (!stackViewport) {
            throw new Error('Failed to get stack viewport');
          }

          if (
            typeof (stackViewport as unknown as Types.IVideoViewport).play ===
            'function'
          ) {
            throw new Error(
              'Viewport type mismatch: expected STACK but got VIDEO'
            );
          }

          await stackViewport.setStack(memoizedImageIds);

          // Bail if a newer DICOM switch superseded this setup
          if (!isMounted || isStale()) {
            if (transitionSnapshot) transitionSnapshot.remove();
            return;
          }

          // Restore per-DICOM VOI, or reset to defaults on reuse path
          if (dicom?.uuid) {
            const savedVoi = voiMapRef.current?.get(dicom.uuid);
            if (savedVoi) {
              stackViewport.setProperties({ voiRange: savedVoi });
            } else if (isReuse) {
              stackViewport.setProperties({
                voiRange: { lower: 0, upper: 255 },
              });
            }
          }

          stackViewport.render();
          stackViewport.resetCamera();

          // Stack viewports don't need saved camera — resetCamera is reliable.
          // Remove any stale entry from a prior video viewport on this ID.
          viewportInitialCameraRef.current.delete(viewportId);

          element.dataset.rendered = 'true';
          hideOldFrameRef.current = false;

          // Remove type-change snapshot — new image is now painted
          if (transitionSnapshot) {
            transitionSnapshot.remove();
            transitionSnapshot = null;
          }

          if (isMounted && !isStale()) {
            setViewport(stackViewport);
            setIsLoadingViewport(false);
            loadedDicomUuidRef.current = dicom?.uuid;
            setIsDicomTransitioning(false);

            requestAnimationFrame(() => {
              if (isMounted && !isStale()) {
                try {
                  stackViewport.render();
                } catch {
                  // Viewport might be disposed
                }
              }
            });
          }
        }
      } catch (err) {
        // Clean up any snapshot on error
        if (transitionSnapshot) {
          transitionSnapshot.remove();
          transitionSnapshot = null;
        }
        if (snapshotCanvas) {
          snapshotCanvas.remove();
          snapshotCanvas = null;
        }
        if (isMounted && !isStale()) {
          // Log the failure for diagnostics. viewportError is still set so the
          // pane stops loading and renders empty — but it's no longer surfaced
          // as a red error overlay (see ViewerPane).
          // eslint-disable-next-line no-console
          console.error('[ViewerPane] Failed to initialize viewport', {
            viewportId,
            dicomUuid: dicom?.uuid,
            error: err,
          });
          setViewportError(err instanceof Error ? err : new Error(String(err)));
          setFailedDicomUuid(dicom?.uuid);
          setIsLoadingViewport(false);
          setIsDicomTransitioning(false);
        }
      }
    }

    setupViewport();

    return () => {
      isMounted = false;
      if (videoMetadataProviderRef.current) {
        metaData.removeProvider(videoMetadataProviderRef.current);
        videoMetadataProviderRef.current = null;
      }
      setIsLoadingViewport(false);
      // Don't clear isDicomTransitioning here — this cleanup runs on dependency
      // changes (including DICOM switch) before the new viewport finishes loading.
      // Clearing it prematurely would flash stale/blank content. The flag is
      // cleared inside setupViewport() once loading actually completes.
    };
  }, [
    renderingEngine,
    viewportId,
    memoizedImageIds,
    isVideo,
    videoUrl,
    imageUrl,
    dicom,
    frameCount,
    autoplay,
    startFrame,
    cornerstoneRef,
    viewportCreatedRef,
    loadedDicomUuidRef,
    videoMetadataProviderRef,
    viewportInitialCameraRef,
    voiMapRef,
    setIsDicomTransitioning,
  ]);

  // Cleanup viewport only on unmount
  useEffect(
    () => () => {
      viewportCreatedRef.current = false;
      if (renderingEngine) {
        try {
          renderingEngine.disableElement(viewportId);
        } catch {
          // Viewport may already be disabled
        }
      }
      setViewport(null);
    },
    [renderingEngine, viewportId, viewportCreatedRef]
  );

  // Check if this is a video viewport type (native video playback)
  const isVideoViewport = useMemo(() => {
    if (!viewport) return false;
    return (
      typeof (viewport as Types.IVideoViewport).play === 'function' &&
      typeof (viewport as Types.IVideoViewport).pause === 'function'
    );
  }, [viewport]);

  return {
    viewport,
    isLoadingViewport,
    viewportError,
    failedDicomUuid,
    isVideoViewport,
    frameImageIds,
    memoizedImageIds,
  };
}
